import React, {useMemo} from 'react';
import {
  AbsoluteCenter,
  Box,
  Button,
  Flex,
  ListItem,
  Menu,
  MenuButton,
  MenuDivider,
  MenuItem,
  MenuList,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  UnorderedList,
  useDisclosure,
} from '@chakra-ui/react';
import {PageHeader} from '../PageHeader';
import {TagQueryKeys, useTags} from '../../hooks/api/tags';
import {useQueries} from '@tanstack/react-query';
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import {TagDetailsResponse, TagResponse, TagTypeLabel} from '../../shared/tags';
import {pluralize} from '../../lib/helpers';
import {filterNull} from '../../shared/util';
import {TagEditorModal} from './TagEditorModal';
import {
  CaretDown,
  CaretUp,
  ChevronDown,
  OverflowMenuVertical,
} from '@carbon/icons-react';
import {useNavigate} from 'react-router-dom';
import {EnrichAccountsModal} from '../AccountGrid/header/EnrichButton';
import {useScoringInfo} from '../../hooks/api/scoringInfo';
import {DeleteTagModal} from './DeleteTagModal';
import {WebResearcherIcon} from '../Icons/WebResearcher';
import {CustomerAccountsImportModal} from '../Accounts/CustomerAccountsImport';
import {
  useIsFeatureEnabled,
  useIsFreeOrListBuilderCustomer,
} from '../../hooks/api/metadata';
import {useAccountLimitsForImport} from '../../hooks/useAccountLimitsForImport';
import {createAccountFilter} from '../../shared/filter/accountFilter';
import {CompanyLogo} from '../Icons/CompanyLogo';
import {GetTagDetails} from '../../shared/api/definitions/tags';
import {buildApiQueryFn} from '../../hooks/api/api';
import {useKeyplayApi} from '../../context/ApiContext';
import {DynamicListEditorModal} from './DynamicListEditorModal';
import {Bold} from '../text';

type TagRow = TagResponse & Partial<TagDetailsResponse>;

const EmptyAccountLists = () => {
  return (
    <Flex
      direction="column"
      align="center"
      p="16"
      gap="4"
      maxW="700px"
      alignSelf="center"
    >
      <WebResearcherIcon h="160px" w="160px" />
      <Text>No lists created yet</Text>
      <UnorderedList>
        <ListItem>
          Create a new <Bold>static list</Bold> to manually add your existing
          accounts.
        </ListItem>
        <ListItem>
          Create a new <Bold>dynamic list</Bold> to filter and organize
          accounts.
        </ListItem>
        <ListItem>
          Import a list to create a <Bold>static list</Bold> for all accounts
          included in that list.
        </ListItem>
      </UnorderedList>
    </Flex>
  );
};

const useAllTagDetails = (tags: TagResponse[]) => {
  const makeApiCall = useKeyplayApi();
  const detailQueries = useQueries({
    queries: tags.map((tag) => ({
      queryKey: TagQueryKeys.detailsById(tag._id),
      queryFn: buildApiQueryFn(makeApiCall, GetTagDetails, {tagId: tag._id}),
    })),
    combine: (results) => results.map((result) => result.data),
  });

  return useMemo(
    () =>
      new Map(
        filterNull(
          tags.map((tag, i) => {
            const data = detailQueries[i];
            return data ? [tag._id.toString(), data] : null;
          })
        )
      ),
    [tags, detailQueries]
  );
};

export const AccountLists = () => {
  const tags = useTags({});
  // Fetch the tag details lazily to avoid long initial load time.
  const detailsMap = useAllTagDetails(tags.data ?? []);

  const createTagModal = useDisclosure();
  const createDynamicListModal = useDisclosure();
  const importModal = useDisclosure();
  const showImportModal = useIsFeatureEnabled('importsTab');
  const accountLimits = useAccountLimitsForImport();

  // Combine the tag details with the tags data to provide to the table component to enable sorting
  // by number of accounts.
  const tagRows: TagRow[] = useMemo(
    () =>
      tags.data?.map((tag) => ({
        ...tag,
        ...detailsMap.get(tag._id.toString()),
      })) ?? [],
    [tags.data, detailsMap]
  );

  if (tags.isLoading) {
    return <InitialLoading />;
  }

  return (
    <Flex direction="column">
      <PageHeader title="Account Lists">
        <Flex gap="2">
          <Menu>
            <MenuButton
              as={Button}
              colorScheme="kbuttonblue"
              fontWeight="normal"
              variant="outline"
              rightIcon={<ChevronDown />}
            >
              New List
            </MenuButton>
            <MenuList>
              <MenuItem onClick={createTagModal.onOpen}>Static List</MenuItem>
              <MenuItem onClick={createDynamicListModal.onOpen}>
                Dynamic List
              </MenuItem>
            </MenuList>
          </Menu>
          <TagEditorModal
            isOpen={createTagModal.isOpen}
            onClose={createTagModal.onClose}
          />
          <DynamicListEditorModal
            isOpen={createDynamicListModal.isOpen}
            onClose={createDynamicListModal.onClose}
          />

          {showImportModal && (
            <>
              <Tooltip
                label={
                  accountLimits.isAtLimit
                    ? accountLimits.limitMessage
                    : undefined
                }
                isDisabled={!accountLimits.isAtLimit}
              >
                <Button
                  colorScheme="kbuttonblue"
                  fontWeight="normal"
                  onClick={importModal.onOpen}
                  isDisabled={accountLimits.isAtLimit}
                >
                  Import List
                </Button>
              </Tooltip>
              <CustomerAccountsImportModal
                isOpen={importModal.isOpen}
                onClose={importModal.onClose}
              />
            </>
          )}
        </Flex>
      </PageHeader>

      <Flex
        direction="column"
        gap="4"
        p="4"
        w="100%"
        h="100%"
        maxW="1000px"
        alignSelf="center"
      >
        <Box textColor="kgray.300" fontSize="sm">
          {pluralize(tagRows.length, 'list', 'lists')}
        </Box>

        <AccountListsTable tags={tagRows} />
        {!tagRows.length && <EmptyAccountLists />}
      </Flex>
    </Flex>
  );
};

const columnHelper = createColumnHelper<TagRow>();
const columns = [
  columnHelper.accessor('name', {
    header: 'Name',
    size: 350,
    cell: (info) => info.getValue(),
  }),
  columnHelper.accessor('type', {
    header: 'Type',
    size: 200,
    cell: (info) => (
      <Text color="kgray.300">{TagTypeLabel[info.getValue()]}</Text>
    ),
  }),
  columnHelper.accessor('numAccounts', {
    id: 'accounts',
    header: 'Accounts',
    size: 50,
    sortUndefined: -1,
    cell: (info) => (
      <Text color="kgray.300">{info.getValue()?.toLocaleString() ?? '-'}</Text>
    ),
  }),
  columnHelper.accessor('previewAccounts', {
    id: 'preview',
    header: 'Preview',
    size: 50,
    cell: (info) => <TagAccountsPreview previewAccounts={info.getValue()} />,
    enableSorting: false,
  }),
  columnHelper.accessor((tag) => tag, {
    id: 'actions',
    header: '',
    size: 50,
    cell: (info) => <TagMenu tag={info.getValue()} />,
    enableSorting: false,
  }),
];

const TagMenu = ({tag}: {tag: TagRow}) => {
  const tagEditorModal = useDisclosure();
  const dynamicListEditorModal = useDisclosure();
  const deleteTagModal = useDisclosure();
  const enrichAccountsModal = useDisclosure();
  const navigate = useNavigate();
  const isFreeOrListBuilderCustomer = useIsFreeOrListBuilderCustomer();

  const handleViewAccounts = () => {
    const queryParams = new URLSearchParams();
    queryParams.set('tag', tag._id.toString());
    navigate({
      pathname: isFreeOrListBuilderCustomer
        ? '/accounts/saved'
        : '/accounts/all',
      search: queryParams.toString(),
    });
  };
  const {data: scoringInfo} = useScoringInfo();
  const scoringRunId = scoringInfo?.latestRefresh?.runId;

  return (
    <Box textAlign="end">
      <Menu>
        <MenuButton as={Button} variant="ghost" size="sm" p="2">
          <OverflowMenuVertical />
        </MenuButton>
        <MenuList>
          {tag.type === 'dynamic' ? (
            <MenuItem onClick={dynamicListEditorModal.onOpen}>
              Edit Filters
              <DynamicListEditorModal
                tag={tag}
                isOpen={dynamicListEditorModal.isOpen}
                onClose={dynamicListEditorModal.onClose}
              />
            </MenuItem>
          ) : (
            <MenuItem onClick={tagEditorModal.onOpen}>
              Edit List Name
              <TagEditorModal
                tag={tag}
                isOpen={tagEditorModal.isOpen}
                onClose={tagEditorModal.onClose}
              />
            </MenuItem>
          )}
          <MenuItem textColor="kred.500" onClick={deleteTagModal.onOpen}>
            Delete List
            <DeleteTagModal
              tag={tag}
              isOpen={deleteTagModal.isOpen}
              onClose={deleteTagModal.onClose}
            />
          </MenuItem>
          <MenuDivider mx="3" />
          <MenuItem onClick={handleViewAccounts}>View Accounts</MenuItem>
          {!isFreeOrListBuilderCustomer && (
            <MenuItem
              onClick={enrichAccountsModal.onOpen}
              isDisabled={!scoringRunId}
            >
              Enrich Accounts
              {scoringRunId && (
                <EnrichAccountsModal
                  accountSelection={{
                    name: 'query',
                    query: {
                      filter: createAccountFilter([
                        {operator: 'in', field: 'tags', value: [tag._id]},
                      ]),
                      runId: scoringRunId,
                    },
                  }}
                  isOpen={enrichAccountsModal.isOpen}
                  onClose={enrichAccountsModal.onClose}
                  modalTitle={`Enrich List Accounts: ${tag.name}`}
                />
              )}
            </MenuItem>
          )}
        </MenuList>
      </Menu>
    </Box>
  );
};

const TagAccountsPreview = ({
  previewAccounts,
}: {
  previewAccounts: TagRow['previewAccounts'];
}) => {
  if (!previewAccounts?.length) {
    return null;
  }

  return (
    <Flex gap="1">
      {previewAccounts.map((account) => (
        <Tooltip
          key={account._id.toString()}
          label={account.name}
          placement="top"
        >
          <Box borderRadius="md" overflow="hidden" flexShrink="0">
            <CompanyLogo
              domain={account.domain}
              name={account.name}
              boxSize="24px"
            />
          </Box>
        </Tooltip>
      ))}
    </Flex>
  );
};

const AccountListsTable = ({tags}: {tags: TagRow[]}) => {
  const [sorting, setSorting] = React.useState([{id: 'name', desc: false}]);

  const table = useReactTable({
    columns,
    data: tags ?? [],
    getCoreRowModel: getCoreRowModel(),
    getRowId: (row) => row._id.toString(),
    getSortedRowModel: getSortedRowModel(),
    enableSorting: tags.length > 0,
    state: {sorting},
    onSortingChange: setSorting,
    enableSortingRemoval: false,
    defaultColumn: {
      minSize: 0,
      size: 150,
    },
  });

  return (
    <TableContainer border="1px" borderColor="kgray.200" borderRadius="md">
      <Table variant="simple">
        <Thead bg="kgray.100">
          {table.getHeaderGroups().map((headerGroup) => (
            <Tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => (
                <Th
                  key={header.id}
                  w={header.getSize() ? `${header.getSize()}px` : 'auto'}
                  cursor={header.column.getCanSort() ? 'pointer' : undefined}
                  onClick={header.column.getToggleSortingHandler()}
                >
                  <Flex alignItems="center" gap="1">
                    {flexRender(
                      header.column.columnDef.header,
                      header.getContext()
                    )}
                    {header.column.getCanSort() && (
                      <Box display="inline-block">
                        {{
                          asc: <CaretUp />,
                          desc: <CaretDown />,
                        }[header.column.getIsSorted().toString()] ?? ''}
                      </Box>
                    )}
                  </Flex>
                </Th>
              ))}
            </Tr>
          ))}
        </Thead>
        <Tbody>
          {table.getRowModel().rows.map((row) => (
            <Tr key={row.id} role="group">
              {row.getVisibleCells().map((cell) => (
                <Td
                  key={cell.id}
                  _groupHover={{bg: 'kgray.100'}}
                  w={
                    cell.getContext().column.getSize()
                      ? `${cell.getContext().column.getSize()}px`
                      : 'auto'
                  }
                >
                  {flexRender(cell.column.columnDef.cell, cell.getContext())}
                </Td>
              ))}
            </Tr>
          ))}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const InitialLoading = () => {
  return (
    <Flex flex="1">
      <AbsoluteCenter>
        <Spinner color="kred.300" size="lg" />
      </AbsoluteCenter>
    </Flex>
  );
};
